/**
 * @jeanieiq/i18n — shared translation runtime for JeanieIQ.
 *
 * Session 0.6 full build. Pure-TypeScript translate function with static
 * JSON imports — no dependency on i18next in the shared package. Both
 * apps/api and any other Node-side consumer call `translate()` here
 * directly; apps/web has its own react-i18next setup for React hooks
 * but loads the exact same JSON files (synced at build time via
 * vite-plugin-static-copy from `packages/i18n/locales/`).
 *
 * Key features:
 *   - Static JSON imports (works in Node + browser bundlers, no fs dep)
 *   - Nested-object navigation via dot-path (`common.buttons.save`)
 *   - `{{var}}` interpolation
 *   - i18next-style pluralisation via `_one` / `_other` suffixes keyed on
 *     `{ count }` interpolation
 *   - Fallback chain: requested locale → en-GB → key itself
 *   - `translate()` NEVER throws on unknown keys — returns the key so UI
 *     doesn't blow up on a missing string
 *
 * See specs/16-i18n.md (v1.1) plus Session 0.6 follow-up to bump it to v1.2
 * with the 8-namespace + snake_case conventions.
 */

// ---------------------------------------------------------------------------
// Static JSON imports — en-GB is the complete source of truth. Other locales
// import only the namespaces they override.
// ---------------------------------------------------------------------------

import enGbCommon from '../locales/en-GB/common.json';
import enGbCompliance from '../locales/en-GB/compliance.json';
import enGbHr from '../locales/en-GB/hr.json';
import enGbFinance from '../locales/en-GB/finance.json';
import enGbAuth from '../locales/en-GB/auth.json';
import enGbErrors from '../locales/en-GB/errors.json';
import enGbEmails from '../locales/en-GB/emails.json';
import enGbNotifications from '../locales/en-GB/notifications.json';
import enGbAdministration from '../locales/en-GB/administration.json';
import enGbDashboard from '../locales/en-GB/dashboard.json';
import enGbGuidance from '../locales/en-GB/guidance.json';
import enGbHowTo from '../locales/en-GB/howTo.json';
import enGbStock from '../locales/en-GB/stock.json';
import enGbDirectory from '../locales/en-GB/directory.json';
import enGbGlossary from '../locales/en-GB/glossary.json';
import enGbTraining from '../locales/en-GB/training.json';
import enGbHealthSafety from '../locales/en-GB/healthSafety.json';
import enGbComplianceGate from '../locales/en-GB/complianceGate.json';
import enGbRota from '../locales/en-GB/rota.json';
import enGbCustomerCrm from '../locales/en-GB/customerCrm.json';
import enGbApprovedSuppliers from '../locales/en-GB/approvedSuppliers.json';
import enGbHoliday from '../locales/en-GB/holiday.json';
import enGbEnquiriesQuotations from '../locales/en-GB/enquiriesQuotations.json';
import enGbBuildingMaintenance from '../locales/en-GB/buildingMaintenance.json';
import enGbAccreditations from '../locales/en-GB/accreditations.json';
import enGbFleet from '../locales/en-GB/fleet.json';
import enGbPlantEquipment from '../locales/en-GB/plantEquipment.json';
import enGbAssetsMaintenance from '../locales/en-GB/assetsMaintenance.json';
import enGbJobs from '../locales/en-GB/jobs.json';
import enGbErpAccounts from '../locales/en-GB/erpAccounts.json';
import enGbPayroll from '../locales/en-GB/payroll.json';
import enGbPharmacy from '../locales/en-GB/pharmacy.json';
import enGbContracts from '../locales/en-GB/contracts.json';
import enGbTutorial from '../locales/en-GB/tutorial.json';
import enGbLocum from '../locales/en-GB/locum.json';
import enGbEmployeeApp from '../locales/en-GB/employeeApp.json';
import enGbKiosk from '../locales/en-GB/kiosk.json';
import enGbDriverTablet from '../locales/en-GB/driverTablet.json';
import enGbWls from '../locales/en-GB/wls.json';
import enGbMotorTrade from '../locales/en-GB/motorTrade.json';
import enGbInformationSecurity from '../locales/en-GB/informationSecurity.json';
import enGbMarketing from '../locales/en-GB/marketing.json';

import enAuAdministration from '../locales/en-AU/administration.json';
import enAuApprovedSuppliers from '../locales/en-AU/approvedSuppliers.json';
import enAuAssetsMaintenance from '../locales/en-AU/assetsMaintenance.json';
import enAuBuildingMaintenance from '../locales/en-AU/buildingMaintenance.json';
import enAuCommon from '../locales/en-AU/common.json';
import enAuComplianceGate from '../locales/en-AU/complianceGate.json';
import enAuContracts from '../locales/en-AU/contracts.json';
import enAuCustomerCrm from '../locales/en-AU/customerCrm.json';
import enAuDashboard from '../locales/en-AU/dashboard.json';
import enAuDriverTablet from '../locales/en-AU/driverTablet.json';
import enAuEmployeeApp from '../locales/en-AU/employeeApp.json';
import enAuFinance from '../locales/en-AU/finance.json';
import enAuFleet from '../locales/en-AU/fleet.json';
import enAuGlossary from '../locales/en-AU/glossary.json';
import enAuGuidance from '../locales/en-AU/guidance.json';
import enAuHealthSafety from '../locales/en-AU/healthSafety.json';
import enAuHoliday from '../locales/en-AU/holiday.json';
import enAuHowTo from '../locales/en-AU/howTo.json';
import enAuHr from '../locales/en-AU/hr.json';
import enAuInformationSecurity from '../locales/en-AU/informationSecurity.json';
import enAuMotorTrade from '../locales/en-AU/motorTrade.json';
import enAuPharmacy from '../locales/en-AU/pharmacy.json';
import enAuPlantEquipment from '../locales/en-AU/plantEquipment.json';
import enAuTraining from '../locales/en-AU/training.json';
import enIeAdministration from '../locales/en-IE/administration.json';
import enIeApprovedSuppliers from '../locales/en-IE/approvedSuppliers.json';
import enIeAssetsMaintenance from '../locales/en-IE/assetsMaintenance.json';
import enIeBuildingMaintenance from '../locales/en-IE/buildingMaintenance.json';
import enIeCommon from '../locales/en-IE/common.json';
import enIeComplianceGate from '../locales/en-IE/complianceGate.json';
import enIeContracts from '../locales/en-IE/contracts.json';
import enIeCustomerCrm from '../locales/en-IE/customerCrm.json';
import enIeDashboard from '../locales/en-IE/dashboard.json';
import enIeDriverTablet from '../locales/en-IE/driverTablet.json';
import enIeEmployeeApp from '../locales/en-IE/employeeApp.json';
import enIeEnquiriesQuotations from '../locales/en-IE/enquiriesQuotations.json';
import enIeFleet from '../locales/en-IE/fleet.json';
import enIeGlossary from '../locales/en-IE/glossary.json';
import enIeGuidance from '../locales/en-IE/guidance.json';
import enIeHealthSafety from '../locales/en-IE/healthSafety.json';
import enIeHoliday from '../locales/en-IE/holiday.json';
import enIeHowTo from '../locales/en-IE/howTo.json';
import enIeHr from '../locales/en-IE/hr.json';
import enIeInformationSecurity from '../locales/en-IE/informationSecurity.json';
import enIeKiosk from '../locales/en-IE/kiosk.json';
import enIeMotorTrade from '../locales/en-IE/motorTrade.json';
import enIePharmacy from '../locales/en-IE/pharmacy.json';
import enIePlantEquipment from '../locales/en-IE/plantEquipment.json';
import enIeRota from '../locales/en-IE/rota.json';
import enIeTraining from '../locales/en-IE/training.json';

// ---------------------------------------------------------------------------
// Constants + types
// ---------------------------------------------------------------------------

/**
 * All namespaces. Session 0.6 added 'notifications' as the eighth, Session
 * 2.1 added 'dashboard' as the ninth, Session 2.2 adds 'guidance' as the
 * tenth so the Guidance Engine has its own home for ~150 keys (tabs,
 * tiers, library hero/stats/disclaimer, contextual panel labels, wizard
 * UI, modal disclaimer, group labels, badges).
 * Spec-16 should be bumped to v1.4 to document this.
 */
export const NAMESPACES = [
  'common',
  'compliance',
  'hr',
  'finance',
  'auth',
  'emails',
  'errors',
  'notifications',
  'dashboard',
  'administration',
  'guidance',
  'howTo',
  'stock',
  'directory',
  'glossary',
  'training',
  'healthSafety',
  'complianceGate',
  'rota',
  'customerCrm',
  'approvedSuppliers',
  'holiday',
  'enquiriesQuotations',
  'buildingMaintenance',
  'accreditations',
  'fleet',
  'plantEquipment',
  // 2026-09-29 - the cross-register Maintenance Schedule (`/maintenance-schedule`),
  // which spans plant, calibration, vehicles and building, so it belongs to no
  // one register's namespace.
  'assetsMaintenance',
  'jobs',
  'erpAccounts',
  'payroll',
  /*
   * pharmacy, 2026-09-25. The section had 200 .tsx files and ~33,000 lines of
   * hardcoded English with no namespace at all, while its devolved ENG/WLS
   * routers were already built - the Welsh routes existed and the Welsh words
   * did not. Converted a slice at a time; see i18n-key-guard for what is
   * covered so far.
   */
  'pharmacy',
  // 2026-10-03 - the contract signing panel, its tick-box and the upload form.
  'contracts',
  // 2026-09-21 (TUTORIAL-STATE-001) — the first-login walk round the platform.
  // Copy for the invitation pop-up and the ten tour steps; deliberately its
  // own namespace and not part of `common`, because it is the only text on the
  // platform that a user sees exactly twice and then never again.
  'tutorial',
  // 2026-09-24 — the locum app (/locum), moved off hardcoded English.
  'locum',
  // 2026-09-24 — the Employee app (/employee), built on the bundle from day one.
  'employeeApp',
  /* The wall terminal and its provisioning page. Kept out of
     `complianceGate`, which is the admin screen: the two share no sentence,
     so merging them would silently reword one of the surfaces. */
  'kiosk',
  /* The in-cab tablet and the DVSA walkaround lists. Kept out of `fleet` so
     the cab's wording is translated and reviewed apart from the desk's —
     NOT to save a download, since every namespace is in the same bundle. */
  'driverTablet',
  // 2026-08-24 nation split — Welsh terminology overlay. Holds ONLY the terms
  // that differ from the UK-wide `common` wording ("health board", not "ICB");
  // anything absent falls through to common, so this file must never become a
  // copy of it.
  'wls',
  // 2026-10-05 — Motor Trade, which had 52 .tsx files and no namespace at all
  // while every other section had one. Converted a file at a time; see the
  // i18n key guard for how far the coverage reaches.
  'motorTrade',
  // 2026-10-21 — Information Security: the digital asset register and the
  // incident and breach record. Built on the bundle from day one.
  'informationSecurity',
  // Marketing: the library, planned social posts and their calendar.
  'marketing',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

/** Phase 1 UK launch uses en-GB. Others add as content work in Phase 2+. */
export const DEFAULT_LOCALE = 'en-GB';

/** All Phase 1–5 locales (per specs/16-i18n.md table). */
export const PHASE_1_TO_5_LOCALES = ['en-GB', 'en-AU', 'en-IE', 'en-CA', 'en-US'] as const;

export type Phase15Locale = (typeof PHASE_1_TO_5_LOCALES)[number];

/** A loose locale type — any string is accepted; unknown locales fall back. */
export type Locale = string;

// ---------------------------------------------------------------------------
// Locale registry — the runtime lookup source
// ---------------------------------------------------------------------------

/**
 * Locale registry. Each locale declares ONLY the namespaces it actually
 * overrides — unmentioned namespaces fall back to en-GB automatically at
 * translate() time.
 *
 * en-GB has all 8 namespaces populated (source of truth).
 * en-AU overrides the strings that name UK law, bodies or schemes, for the
 * screens an Australian company reaches, and the invoice words in `finance`.
 * en-IE overrides the strings that name UK law, bodies or schemes, for the
 * screens an Irish company reaches. en-CA and en-US have no overrides yet.
 */
type NamespaceMap = Partial<Record<Namespace, Record<string, unknown>>>;

const locales: Record<string, NamespaceMap> = {
  'en-GB': {
    common: enGbCommon as Record<string, unknown>,
    compliance: enGbCompliance as Record<string, unknown>,
    hr: enGbHr as Record<string, unknown>,
    finance: enGbFinance as Record<string, unknown>,
    auth: enGbAuth as Record<string, unknown>,
    errors: enGbErrors as Record<string, unknown>,
    emails: enGbEmails as Record<string, unknown>,
    notifications: enGbNotifications as Record<string, unknown>,
    dashboard: enGbDashboard as Record<string, unknown>,
    administration: enGbAdministration as Record<string, unknown>,
    guidance: enGbGuidance as Record<string, unknown>,
    howTo: enGbHowTo as Record<string, unknown>,
    stock: enGbStock as Record<string, unknown>,
    directory: enGbDirectory as Record<string, unknown>,
    glossary: enGbGlossary as Record<string, unknown>,
    training: enGbTraining as Record<string, unknown>,
    healthSafety: enGbHealthSafety as Record<string, unknown>,
    complianceGate: enGbComplianceGate as Record<string, unknown>,
    rota: enGbRota as Record<string, unknown>,
    customerCrm: enGbCustomerCrm as Record<string, unknown>,
    approvedSuppliers: enGbApprovedSuppliers as Record<string, unknown>,
    holiday: enGbHoliday as Record<string, unknown>,
    enquiriesQuotations: enGbEnquiriesQuotations as Record<string, unknown>,
    buildingMaintenance: enGbBuildingMaintenance as Record<string, unknown>,
    accreditations: enGbAccreditations as Record<string, unknown>,
    fleet: enGbFleet as Record<string, unknown>,
    plantEquipment: enGbPlantEquipment as Record<string, unknown>,
    assetsMaintenance: enGbAssetsMaintenance as Record<string, unknown>,
    jobs: enGbJobs as Record<string, unknown>,
    erpAccounts: enGbErpAccounts as Record<string, unknown>,
    payroll: enGbPayroll as Record<string, unknown>,
    pharmacy: enGbPharmacy as Record<string, unknown>,
    contracts: enGbContracts as Record<string, unknown>,
    tutorial: enGbTutorial as Record<string, unknown>,
    locum: enGbLocum as Record<string, unknown>,
    employeeApp: enGbEmployeeApp as Record<string, unknown>,
    kiosk: enGbKiosk as Record<string, unknown>,
    driverTablet: enGbDriverTablet as Record<string, unknown>,
    wls: enGbWls as Record<string, unknown>,
    motorTrade: enGbMotorTrade as Record<string, unknown>,
    informationSecurity: enGbInformationSecurity as Record<string, unknown>,
    marketing: enGbMarketing as Record<string, unknown>,
  },
  'en-AU': {
    administration: enAuAdministration as Record<string, unknown>,
    approvedSuppliers: enAuApprovedSuppliers as Record<string, unknown>,
    assetsMaintenance: enAuAssetsMaintenance as Record<string, unknown>,
    buildingMaintenance: enAuBuildingMaintenance as Record<string, unknown>,
    common: enAuCommon as Record<string, unknown>,
    complianceGate: enAuComplianceGate as Record<string, unknown>,
    contracts: enAuContracts as Record<string, unknown>,
    customerCrm: enAuCustomerCrm as Record<string, unknown>,
    dashboard: enAuDashboard as Record<string, unknown>,
    driverTablet: enAuDriverTablet as Record<string, unknown>,
    employeeApp: enAuEmployeeApp as Record<string, unknown>,
    finance: enAuFinance as Record<string, unknown>,
    fleet: enAuFleet as Record<string, unknown>,
    glossary: enAuGlossary as Record<string, unknown>,
    guidance: enAuGuidance as Record<string, unknown>,
    healthSafety: enAuHealthSafety as Record<string, unknown>,
    holiday: enAuHoliday as Record<string, unknown>,
    howTo: enAuHowTo as Record<string, unknown>,
    hr: enAuHr as Record<string, unknown>,
    informationSecurity: enAuInformationSecurity as Record<string, unknown>,
    motorTrade: enAuMotorTrade as Record<string, unknown>,
    pharmacy: enAuPharmacy as Record<string, unknown>,
    plantEquipment: enAuPlantEquipment as Record<string, unknown>,
    training: enAuTraining as Record<string, unknown>,
  },
  'en-IE': {
    administration: enIeAdministration as Record<string, unknown>,
    approvedSuppliers: enIeApprovedSuppliers as Record<string, unknown>,
    assetsMaintenance: enIeAssetsMaintenance as Record<string, unknown>,
    buildingMaintenance: enIeBuildingMaintenance as Record<string, unknown>,
    common: enIeCommon as Record<string, unknown>,
    complianceGate: enIeComplianceGate as Record<string, unknown>,
    contracts: enIeContracts as Record<string, unknown>,
    customerCrm: enIeCustomerCrm as Record<string, unknown>,
    dashboard: enIeDashboard as Record<string, unknown>,
    driverTablet: enIeDriverTablet as Record<string, unknown>,
    employeeApp: enIeEmployeeApp as Record<string, unknown>,
    enquiriesQuotations: enIeEnquiriesQuotations as Record<string, unknown>,
    fleet: enIeFleet as Record<string, unknown>,
    glossary: enIeGlossary as Record<string, unknown>,
    guidance: enIeGuidance as Record<string, unknown>,
    healthSafety: enIeHealthSafety as Record<string, unknown>,
    holiday: enIeHoliday as Record<string, unknown>,
    howTo: enIeHowTo as Record<string, unknown>,
    hr: enIeHr as Record<string, unknown>,
    informationSecurity: enIeInformationSecurity as Record<string, unknown>,
    kiosk: enIeKiosk as Record<string, unknown>,
    motorTrade: enIeMotorTrade as Record<string, unknown>,
    pharmacy: enIePharmacy as Record<string, unknown>,
    plantEquipment: enIePlantEquipment as Record<string, unknown>,
    rota: enIeRota as Record<string, unknown>,
    training: enIeTraining as Record<string, unknown>,
  },
  'en-CA': {},
  'en-US': {},
};

// ---------------------------------------------------------------------------
// Nested-object navigation + interpolation
// ---------------------------------------------------------------------------

/**
 * Walk a nested object by dot-path. Returns `undefined` for missing paths
 * rather than throwing so `translate()` can fall through to the next locale.
 *
 * Example: `walk({ a: { b: { c: 'x' } } }, 'a.b.c')` → 'x'
 */
function walk(root: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = root;
  for (const part of parts) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Replace `{{var}}` placeholders with values from the interpolations map.
 * Unknown variables are left in place (e.g. `{{missing}}` stays literal).
 * This is a deliberate fail-loud choice — a missing interpolation should
 * be visible in the UI, not silently resolve to an empty string.
 */
function interpolate(template: string, interpolations: Record<string, unknown> | undefined): string {
  if (!interpolations) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (whole, name: string) => {
    const value = interpolations[name];
    if (value === undefined || value === null) return whole;
    return String(value);
  });
}

// ---------------------------------------------------------------------------
// Pluralisation
// ---------------------------------------------------------------------------

/**
 * i18next-style pluralisation. If the caller passes `{ count }`, the lookup
 * first tries `<key>_one` when count === 1 or `<key>_other` otherwise. If
 * neither exists, falls back to the bare `<key>`.
 *
 * Supports only `_one` / `_other` suffixes — enough for English and most
 * western European languages. Full CLDR plural categories (`zero`, `two`,
 * `few`, `many`, `other`) can be added when a locale needs them.
 */
function resolvePluralKey(
  root: Record<string, unknown>,
  key: string,
  count: number | undefined,
): string | undefined {
  if (typeof count === 'number') {
    const suffix = count === 1 ? '_one' : '_other';
    const withSuffix = walk(root, `${key}${suffix}`);
    if (typeof withSuffix === 'string') return withSuffix;
  }
  const bare = walk(root, key);
  return typeof bare === 'string' ? bare : undefined;
}

// ---------------------------------------------------------------------------
// Core translate function
// ---------------------------------------------------------------------------

export interface TranslateOptions {
  /** Interpolation values — `{{var}}` placeholders in the template. */
  interpolations?: Record<string, unknown>;
  /**
   * Explicit count for pluralisation. Also passed to interpolations as
   * `{{count}}` if that placeholder appears in the template.
   */
  count?: number;
}

/**
 * Translate a key in a given namespace to the requested locale's string.
 *
 * Fallback chain:
 *   1. requested locale + namespace
 *   2. en-GB (default) + namespace
 *   3. the key itself (fail-safe — never throws)
 *
 * The fail-safe is important: UI should never crash on a missing key during
 * development or when a translator hasn't caught up yet. The key is returned
 * literally so the missing string is visible in context.
 *
 * Pluralisation: pass `count` (either in options or as interpolations.count)
 * to pick `<key>_one` / `<key>_other`. `count` is also available as a
 * `{{count}}` interpolation inside the resolved template.
 */
export function translate(
  locale: Locale,
  namespace: Namespace,
  key: string,
  options: TranslateOptions = {},
): string {
  const count =
    options.count ??
    (typeof options.interpolations?.['count'] === 'number'
      ? (options.interpolations['count'] as number)
      : undefined);

  // Merge count into interpolations so {{count}} placeholders resolve.
  const interpolations: Record<string, unknown> = {
    ...(options.interpolations ?? {}),
    ...(count !== undefined ? { count } : {}),
  };

  // Try the requested locale first.
  const requestedNs = locales[locale]?.[namespace];
  if (requestedNs) {
    const hit = resolvePluralKey(requestedNs, key, count);
    if (hit !== undefined) return interpolate(hit, interpolations);
  }

  // Fall back to en-GB unless we're already there.
  if (locale !== DEFAULT_LOCALE) {
    const defaultNs = locales[DEFAULT_LOCALE]?.[namespace];
    if (defaultNs) {
      const hit = resolvePluralKey(defaultNs, key, count);
      if (hit !== undefined) return interpolate(hit, interpolations);
    }
  }

  // Final fallback — return the namespaced key so it shows up in context.
  // Never throw. This behaviour is load-bearing: tests rely on it, and
  // missing keys in dev should be noisy in the UI, not crashes.
  return `${namespace}.${key}`;
}

/**
 * Convenience wrapper that takes a fully-qualified `namespace.key.path` and
 * splits on the first dot. Used by the API error translator where the lookup
 * key is stored as `errors.auth.invalid_credentials`.
 */
export function translateFull(
  locale: Locale,
  fullKey: string,
  options: TranslateOptions = {},
): string {
  const firstDot = fullKey.indexOf('.');
  if (firstDot === -1) return fullKey; // No namespace prefix — return literally
  const ns = fullKey.slice(0, firstDot);
  const rest = fullKey.slice(firstDot + 1);
  if (!(NAMESPACES as readonly string[]).includes(ns)) return fullKey;
  return translate(locale, ns as Namespace, rest, options);
}

/**
 * Returns true if the locale is in the Phase 1–5 list. Does NOT check that
 * the locale has any entries in the registry — unknown locales simply fall
 * back via translate() so a missing registration is a soft failure, not a
 * hard one.
 */
export function isKnownLocale(locale: string): locale is Phase15Locale {
  return (PHASE_1_TO_5_LOCALES as readonly string[]).includes(locale);
}

/**
 * INTERNAL — exposed for tests only. Returns the raw locale registry so
 * tests can enumerate namespaces and check completeness.
 */
export function __getLocaleRegistryForTests(): Record<string, NamespaceMap> {
  return locales;
}

// ---------------------------------------------------------------------------
// Re-exports
// ---------------------------------------------------------------------------

export * from './emails';
